import type { TreatmentType, Phase, PhaseRatios } from '../types';
export type { Phase } from '../types';

// Phase timing ranges from the spec (in minutes)
// We use the midpoint of each range as the base ratio
export const PHASE_TIMING_MINUTES: Record<TreatmentType, Record<Phase, { min: number; max: number }>> = {
  iv: {
    opening: { min: 2, max: 3 },
    building: { min: 3, max: 5 },
    peak: { min: 15, max: 20 },
    longTail: { min: 10, max: 15 },
    closing: { min: 5, max: 10 },
  },
  im: {
    opening: { min: 5, max: 7 },
    building: { min: 7, max: 10 },
    peak: { min: 20, max: 30 },
    longTail: { min: 15, max: 20 },
    closing: { min: 10, max: 15 },
  },
  oral: {
    opening: { min: 10, max: 15 },
    building: { min: 15, max: 20 },
    peak: { min: 30, max: 45 },
    longTail: { min: 20, max: 30 },
    closing: { min: 15, max: 20 },
  },
  spravato: {
    opening: { min: 5, max: 8 },
    building: { min: 8, max: 12 },
    peak: { min: 20, max: 30 },
    longTail: { min: 15, max: 20 },
    closing: { min: 50, max: 70 }, // Extended to cover 2-hour REMS monitoring period
  },
};

// Calculate base ratios using midpoints
function calculateRatios(timing: Record<Phase, { min: number; max: number }>): PhaseRatios {
  const midpoints: Record<Phase, number> = {
    opening: (timing.opening.min + timing.opening.max) / 2,
    building: (timing.building.min + timing.building.max) / 2,
    peak: (timing.peak.min + timing.peak.max) / 2,
    longTail: (timing.longTail.min + timing.longTail.max) / 2,
    closing: (timing.closing.min + timing.closing.max) / 2,
  };

  const total = Object.values(midpoints).reduce((sum, val) => sum + val, 0);

  return {
    opening: midpoints.opening / total,
    building: midpoints.building / total,
    peak: midpoints.peak / total,
    longTail: midpoints.longTail / total,
    closing: midpoints.closing / total,
  };
}

export const PHASE_RATIOS: Record<TreatmentType, PhaseRatios> = {
  iv: calculateRatios(PHASE_TIMING_MINUTES.iv),
  im: calculateRatios(PHASE_TIMING_MINUTES.im),
  oral: calculateRatios(PHASE_TIMING_MINUTES.oral),
  spravato: calculateRatios(PHASE_TIMING_MINUTES.spravato),
};

export const TREATMENT_LABELS: Record<TreatmentType, { name: string; description: string }> = {
  iv: {
    name: 'IV (Intravenous)',
    description: 'Fastest onset, shortest duration. Rapid peak with quick transition.',
  },
  im: {
    name: 'IM (Intramuscular)',
    description: 'Moderate onset, balanced duration. Gradual build to sustained peak.',
  },
  oral: {
    name: 'Oral/Sublingual',
    description: 'Slower onset, longer duration. Extended journey with gentle transitions.',
  },
  spravato: {
    name: 'Spravato/Intranasal',
    description: 'FDA-approved nasal spray. Moderate onset with sustained experience.',
  },
};

export const PHASES_IN_ORDER: Phase[] = ['opening', 'building', 'peak', 'longTail', 'closing'];

export const PHASE_LABELS: Record<Phase, string> = {
  opening: 'Opening',
  building: 'Building',
  peak: 'Peak',
  longTail: 'Softening',
  closing: 'Closing',
};

export const PHASE_DESCRIPTIONS: Record<Phase, string> = {
  opening: 'Gentle entry as effects begin. Calming, grounding music.',
  building: 'Gradual intensification. Rising energy and depth.',
  peak: 'Most profound experience. Emotionally resonant, powerful music.',
  longTail: 'Gentle descent. Softer, reflective transitions.',
  closing: 'Return to awareness. Grounding, familiar sounds.',
};

export const MIN_DURATION_MINUTES = 30;
export const MAX_DURATION_MINUTES = 180;
export const DEFAULT_DURATION_MINUTES = 60;

// Default durations by treatment type (in minutes)
// Based on clinical research for typical music/active experience duration:
// - IV: Standard 40-minute infusion + padding
// - IM: 45-75 min standard dose, 75+ min for higher doses (KAP protocols)
// - Oral: 45-75 min sublingual (spit protocol), longer if swallowed
// - Spravato: Effects peak at 40 min, resolve by 60-90 min; 2-hour REMS monitoring period
export const DEFAULT_DURATIONS: Record<TreatmentType, number> = {
  iv: 60,
  im: 105,
  oral: 105,
  spravato: 120,
};

// Allow up to 30 seconds overflow when filling phases
export const PHASE_OVERFLOW_SECONDS = 30;

// Phase colors for visualization -- organic, muted earth tones
export const PHASE_COLORS: Record<Phase, string> = {
  opening: 'bg-phase-opening',
  building: 'bg-phase-building',
  peak: 'bg-phase-peak',
  longTail: 'bg-phase-softening',
  closing: 'bg-phase-closing',
};

// Phase backdrop colors for radial gradient overlay
export const PHASE_BACKDROP_COLORS: Record<Phase, string> = {
  opening: 'rgba(126, 184, 154, 0.15)',    // soft sage
  building: 'rgba(91, 175, 176, 0.15)',    // warm teal
  peak: 'rgba(155, 126, 207, 0.15)',       // deep amethyst
  longTail: 'rgba(201, 139, 164, 0.15)',   // dusty rose
  closing: 'rgba(212, 164, 84, 0.15)',     // golden amber
};

export function getPhaseColor(phase: Phase): string {
  return PHASE_COLORS[phase] || 'bg-white/50';
}

// RGB values for chart gradients and fills
export const PHASE_COLORS_RGB: Record<Phase, string> = {
  opening: '126, 184, 154',    // soft sage
  building: '91, 175, 176',    // warm teal
  peak: '155, 126, 207',       // deep amethyst
  longTail: '201, 139, 164',   // dusty rose
  closing: '212, 164, 84',     // golden amber
};

// Phase hex colors for direct style usage
export const PHASE_COLORS_HEX: Record<Phase, string> = {
  opening: '#7EB89A',
  building: '#5BAFB0',
  peak: '#9B7ECF',
  longTail: '#C98BA4',
  closing: '#D4A454',
};
